import { normaliseCountryCode } from '../../../../utils/country';

const POPUP_ADS_URL =
  'https://lmicdn.blob.core.windows.net/common/banners/popup_ads.json';

/** This app's code in the feed's `applied_apps` field. */
export const APP_CODE = 'bi';

const fetchAdsJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch popup ads: ${response.status}`);
  }
  return response.json();
};

/** An ad with no `applied_apps`, or one that names 'all', is served everywhere. */
const isAdForThisApp = (ad) => {
  const { applied_apps: apps } = ad;
  return !apps?.length || apps.includes('all') || apps.includes(APP_CODE);
};

/** Without a key, a heading and somewhere to go there is no ad to show. */
const isAdComplete = (ad) => !!(ad.key && ad.title && ad.link);

// The feed is hand-authored, so its codes go through the same normalisation as the user's.
const isAdForCountry = (ad, countryCode, namedCountries) => {
  const countries = (ad.countries || []).map(normaliseCountryCode);
  if (!countries.length || countries.includes('all')) return true;
  if (!countryCode) return false;
  if (countries.includes(countryCode)) return true;
  // 'others' covers every country the feed does not name for itself in its metadata.
  return countries.includes('others') && !namedCountries.includes(countryCode);
};

/** An ad with no `applied_groups` is for every buy group. */
const isAdForBuyGroup = (ad, buyGroupId) => {
  const { applied_groups: groups } = ad;
  if (!groups?.length) return true;
  return (
    buyGroupId !== null &&
    buyGroupId !== undefined &&
    groups.map(String).includes(String(buyGroupId))
  );
};

/**
 * Either end of the window is optional, so an ad can open or close open-endedly. The feed writes
 * ISO dates with an offset; an unparseable one is ignored rather than hiding the ad outright.
 */
const isAdWithinSchedule = (ad, now) => {
  const start = Date.parse(ad.start_date || ad.start_datetime || '');
  const end = Date.parse(ad.end_date || ad.end_datetime || '');

  if (!Number.isNaN(start) && now < start) return false;
  if (!Number.isNaN(end) && now > end) return false;
  return true;
};

/** Popup ads for this country and buy group, from the feed that serves this surface. */
export const fetchPopupAds = async (countryCode, buyGroupId) => {
  const data = await fetchAdsJson(POPUP_ADS_URL);
  const ads = data?.advertisements || [];
  const namedCountries = (data?.metadata?.countries || [])
    .map(normaliseCountryCode)
    .filter((code) => code && code !== 'all' && code !== 'others');
  const country = normaliseCountryCode(countryCode);
  const now = Date.now();

  return ads.filter(
    (ad) =>
      isAdComplete(ad) &&
      isAdForThisApp(ad) &&
      (!ad.placement || ad.placement === 'popup') &&
      isAdForCountry(ad, country, namedCountries) &&
      isAdForBuyGroup(ad, buyGroupId) &&
      isAdWithinSchedule(ad, now)
  );
};

/**
 * The popup takes the ad's own proportions rather than cropping it to a shape of our choosing,
 * which means knowing the artwork's size before the dialog opens. Resolving only once the image
 * has decoded also means it arrives drawn, instead of reflowing in front of the user.
 */
export const loadCoverSize = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () =>
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () =>
      reject(new Error(`Failed to load ad cover: ${src}`));
    image.src = src;
  });
